/**
 * Phase 36 Batch AJ — Analysis Basis Chip.
 *
 * Drop-in small banner that declares which price the analysis is
 * anchored on, matching the Criteria tab's wording the user approved
 * ("perfect."):
 *
 *     "Analyzed at $270,000 (counter-offer)
 *      Listed at $285,000 (-5.3%)"
 *
 * Placed at the top of Risk, Financials, Negotiation, and Reports tabs
 * so the user never has to guess what number drove the numbers below.
 *
 * Three states:
 *   - counter applied → indigo chip ("counter-offer")
 *   - ai recommended  → indigo chip ("AI-recommended offer")
 *   - asking          → slate chip  ("asking price")  [also a fallback
 *                       when no basis fields are set on the deal]
 */
import { Tag, Banknote, Sparkles, Target } from "lucide-react";


const BASIS_COPY = {
  user_target_offer: {
    label: "your counter-offer",
    icon: Banknote,
    chip: "bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200",
    iconBg: "bg-indigo-600 text-white",
  },
  ai_recommended_offer: {
    label: "AI-recommended offer",
    icon: Sparkles,
    chip: "bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200",
    iconBg: "bg-indigo-600 text-white",
  },
  purchase_price: {
    label: "asking price",
    icon: Tag,
    chip: "bg-slate-50 text-slate-700 ring-1 ring-slate-200",
    iconBg: "bg-slate-600 text-white",
  },
};


function fmtMoney(v, currency) {
  if (typeof v !== "number" || !Number.isFinite(v)) return "—";
  const prefix = currency && currency !== "USD" ? `${currency} ` : "$";
  return prefix + v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}


export default function AnalysisBasisChip({ deal, className = "" }) {
  if (!deal) return null;
  const basis = deal.effective_purchase_price_basis || "purchase_price";
  const effectivePrice = Number(deal.purchase_price || 0);
  const originalPrice = Number(deal.original_purchase_price || 0) || null;
  const copy = BASIS_COPY[basis] || BASIS_COPY.purchase_price;
  const Icon = copy.icon;
  const currency = deal.property_currency || "USD";

  // Delta vs listing — only meaningful when basis was flipped AND we
  // have an original snapshot.
  let deltaPct = null;
  if (basis !== "purchase_price" && originalPrice && originalPrice > 0) {
    deltaPct = ((effectivePrice - originalPrice) / originalPrice) * 100;
  }

  return (
    <div
      data-testid="analysis-basis-chip"
      data-basis={basis}
      className={`flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 shadow-sm ${className}`}
    >
      <span
        className={`flex h-7 w-7 flex-none items-center justify-center rounded-lg ${copy.iconBg}`}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Analyzed at
          </span>
          <span
            className="text-sm font-bold tabular-nums text-slate-900"
            data-testid="analysis-basis-price"
          >
            {fmtMoney(effectivePrice, currency)}
          </span>
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${copy.chip}`}
          >
            {copy.label}
          </span>
        </div>
        {originalPrice && originalPrice !== effectivePrice && (
          <p className="mt-0.5 text-[11px] text-slate-500" data-testid="analysis-basis-original">
            <Target className="-mt-0.5 mr-1 inline-block h-3 w-3" />
            Listed at{" "}
            <span className="font-medium tabular-nums text-slate-700">
              {fmtMoney(originalPrice, currency)}
            </span>
            {deltaPct !== null && (
              <span
                className={`ml-1 font-medium ${
                  deltaPct < 0 ? "text-emerald-700" : "text-rose-700"
                }`}
              >
                ({deltaPct >= 0 ? "+" : ""}
                {deltaPct.toFixed(1)}%)
              </span>
            )}
          </p>
        )}
      </div>
    </div>
  );
}
